import type { OrderPayload } from "./api";
import { formatThaiDateTime, statusIcon } from "./format";

const STORE_PHONE = "082-5634999";
const LINE_OA_URL = "https://line.me/R/ti/p/@041szjbs";

function el(html: string): HTMLElement {
  const t = document.createElement("template");
  t.innerHTML = html.trim();
  return t.content.firstElementChild as HTMLElement;
}

function formatPaymentAmount(amount: number | null): string {
  return amount == null ? "รอร้านสรุปยอด" : `${amount.toLocaleString("th-TH")} บาท`;
}

function serviceSummary(order: OrderPayload): string {
  if (!order.service.lines.length) return "";
  return `
    <div class="service-mini">
      ${order.service.lines
        .map((line) => `<p>${line.label}</p>`)
        .join("")}
    </div>
  `;
}

function paymentActionLabel(order: OrderPayload): string {
  if (order.payment.status === "ยังไม่ตั้งยอด") return "รอร้านสรุปยอด";
  if (order.payment.status === "ชำระแล้ว") return "ดูรายละเอียด";
  return "ชำระเงิน";
}

export function renderLoading(): HTMLElement {
  return el(`
    <div class="loading">
      <div class="spinner" aria-hidden="true"></div>
      <p>กำลังโหลด…</p>
    </div>
  `);
}

export function renderBinding(
  onSubmit: (phone: string) => void,
  options?: { demoPrefill?: string },
): HTMLElement {
  const prefill = options?.demoPrefill ?? "";
  const root = el(`
    <div class="shell">
      <header class="brand">
        <h1>Trendy Wash</h1>
        <p>เช็คสถานะผ้า</p>
      </header>
      <section class="card">
        <h2>ผูกเบอร์ครั้งแรก</h2>
        <p class="hint">ใส่เบอร์ที่แจ้งร้านตอนฝากผ้า ครั้งถัดไปกดปุ่มแล้วดูสถานะได้เลย</p>
        <form id="bind-form">
          <label for="phone">เบอร์โทรศัพท์</label>
          <input id="phone" type="tel" inputmode="numeric" autocomplete="tel"
            placeholder="0812345678" maxlength="12" value="${prefill}" required />
          <button type="submit" class="btn btn-primary">ยืนยัน</button>
        </form>
      </section>
    </div>
  `);
  const form = root.querySelector<HTMLFormElement>("#bind-form")!;
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const phone = root.querySelector<HTMLInputElement>("#phone")!.value;
    onSubmit(phone);
  });
  return root;
}

export function renderStatus(order: OrderPayload): HTMLElement {
  return el(`
    <div class="shell">
      <header class="brand">
        <h1>Trendy Wash</h1>
        <p>สถานะผ้าซัก</p>
      </header>
      <section class="card">
        <div class="status-hero">
          <div class="status-icon" aria-hidden="true">${statusIcon(order.status)}</div>
          <p class="status-label">${order.status}</p>
        </div>
        <dl class="meta">
          <div class="meta-row">
            <dt>เลขออเดอร์</dt>
            <dd>${order.orderId}</dd>
          </div>
          ${
            order.service.lines.length
              ? `<div class="meta-row meta-row--stack">
                  <dt>รายการ</dt>
                  <dd>${serviceSummary(order)}</dd>
                </div>`
              : ""
          }
          <div class="meta-row">
            <dt>อัปเดตล่าสุด</dt>
            <dd>${formatThaiDateTime(order.updatedAt)} น.</dd>
          </div>
        </dl>
        <div class="payment-summary">
          <div>
            <p class="payment-summary__label">การชำระเงิน</p>
            <p class="payment-summary__status">${order.payment.status}</p>
            <p class="payment-summary__amount">${formatPaymentAmount(order.payment.amountBaht)}</p>
          </div>
          <a class="btn btn-primary payment-summary__btn" href="/payment.html?orderId=${encodeURIComponent(order.orderId)}">
            ${paymentActionLabel(order)}
          </a>
        </div>
      </section>
    </div>
  `);
}

export function renderEmpty(message: string): HTMLElement {
  return el(`
    <div class="shell">
      <header class="brand">
        <h1>Trendy Wash</h1>
        <p>เช็คสถานะผ้า</p>
      </header>
      <section class="card message">
        <p>${message}</p>
        <p>โทรร้าน <a class="tel" href="tel:${STORE_PHONE.replace(/-/g, "")}">${STORE_PHONE}</a></p>
      </section>
    </div>
  `);
}

export function renderError(message: string): HTMLElement {
  const node = renderEmpty(message);
  node.querySelector(".card")?.classList.add("error");
  return node;
}

export function renderLineEntryInstruction(options: {
  title: string;
  subtitle: string;
  richMenuLabel: string;
}): HTMLElement {
  return el(`
    <div class="shell">
      <header class="brand">
        <h1>Trendy Wash</h1>
        <p>${options.subtitle}</p>
      </header>
      <section class="card message line-entry">
        <h2>${options.title}</h2>
        <p>หน้านี้ต้องเปิดจาก LINE ร้าน Trendy Wash</p>
        <p>
          กดปุ่ม <strong>${options.richMenuLabel}</strong>
          ในเมนูด้านล่างของแชทร้านอีกครั้ง
        </p>
        <a class="btn btn-primary line-entry__btn" href="${LINE_OA_URL}">
          เปิด LINE ร้าน
        </a>
        <p>
          หากยังเปิดไม่ได้ โทรร้าน
          <a class="tel" href="tel:${STORE_PHONE.replace(/-/g, "")}">
            ${STORE_PHONE}
          </a>
        </p>
      </section>
    </div>
  `);
}
